import React from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Fade
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { CMChip } from '../components/common/CMChip';

// ============================================================================
// Types
// ============================================================================

interface AttributeRenderingProps {
  item: any;
  showAdditionalProperties: boolean;
  onToggleAdditionalProperties: () => void;
}

interface FormDataMainViewProps {
  item: any;
}

// ============================================================================
// Field Formatting Utilities
// ============================================================================

/**
 * Format field name to be more readable (similar to HTML popup)
 */
export const formatFieldName = (key: string): string => {
  return key
    // Remove any curly braces
    .replace(/[{}]/g, '')
    // Convert snake_case to Title Case
    .replace(/_/g, ' ')
    // Capitalize first letter of each word
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

/**
 * Simplified format function for main view - clean, minimal formatting
 */
export const formatValueSimple = (value: any, key: string): React.ReactNode => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return <Typography component="span" color="text.secondary" fontStyle="italic">—</Typography>;
  }
  
  // Handle boolean values
  if (typeof value === 'boolean') {
    return (
      <Typography component="span" color={value ? "success.main" : "text.secondary"}>
        {value ? 'Yes' : 'No'}
      </Typography>
    );
  }
  
  // Special handling for merged "_other" field values
  if (typeof value === 'string' && (value.includes(' | Other: ') || value.startsWith('Other: '))) {
    if (value.includes(' | Other: ')) {
      const [mainValue, otherPart] = value.split(' | Other: ');
      return (
        <Box component="span">
          <Typography component="span" sx={{ fontWeight: 'medium' }}>
            {mainValue}
          </Typography>
          <Typography component="span" sx={{ 
            display: 'block', 
            mt: 0.5, 
            color: 'info.main', 
            fontStyle: 'italic',
            fontSize: '0.9em'
          }}>
            Other: {otherPart}
          </Typography>
        </Box>
      );
    } else if (value.startsWith('Other: ')) {
      const otherValue = value.replace('Other: ', '');
      return (
        <Typography component="span" sx={{ 
          color: 'info.main', 
          fontStyle: 'italic',
          fontWeight: 'medium'
        }}>
          Other: {otherValue}
        </Typography>
      );
    }
  }
  
  // Handle arrays - simple chip display
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Typography component="span" color="text.secondary" fontStyle="italic">—</Typography>;
    }
    
    // For photo arrays, show count
    if (value.some(item => item && typeof item === 'object' && 'photo_id' in item)) {
      return (
        <Typography component="span" sx={{ fontWeight: 'medium' }}>
          {value.length} photo{value.length !== 1 ? 's' : ''}
        </Typography>
      );
    }
    
    // For record links, show count
    if (value.some(item => item && typeof item === 'object' && 'record_id' in item)) {
      return (
        <Typography component="span" sx={{ fontWeight: 'medium' }}>
          {value.length} linked record{value.length !== 1 ? 's' : ''}
        </Typography>
      );
    }
    
    // Regular arrays - show as chips if not too many
    if (value.length <= 5) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.map((item, index) => {
            const itemStr = String(item);
            const isOtherValue = itemStr.startsWith('Other: ');
            return (
              <CMChip 
                key={index} 
                label={isOtherValue ? itemStr.replace('Other: ', '') : itemStr}
                colorScheme={isOtherValue ? "primary" : "secondary"} 
                sx={{ 
                  fontSize: '0.75rem',
                  fontStyle: isOtherValue ? 'italic' : 'normal'
                }} 
              />
            );
          })}
        </Box>
      );
    } else {
      return (
        <Typography component="span" sx={{ fontWeight: 'medium' }}>
          {value.length} items
        </Typography>
      );
    }
  }
  
  // Handle objects - simplified display
  if (typeof value === 'object') {
    if (value.type && value.coordinates && value.type === 'Point') {
      return (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
          {value.coordinates[1]?.toFixed(6)}, {value.coordinates[0]?.toFixed(6)}
        </Typography>
      );
    }
    return (
      <Typography component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        Object ({Object.keys(value).length} properties)
      </Typography>
    );
  }
  
  // Special handling for dates
  if (
    typeof value === 'string' &&
    (key.toLowerCase().includes('date') || key.toLowerCase().includes('time') || key.toLowerCase().includes('created') || key.toLowerCase().includes('updated')) &&
    !isNaN(Date.parse(value))
  ) {
    const date = new Date(value);
    return (
      <Typography component="span">
        {date.toLocaleDateString()} {date.toLocaleTimeString()}
      </Typography>
    );
  }
  
  // Default - return as simple string
  return (
    <Typography component="span" sx={{ 
      fontWeight: typeof value === 'string' && value.length < 50 ? 'medium' : 'normal',
      wordBreak: 'break-word'
    }}>
      {String(value)}
    </Typography>
  );
};

/**
 * Detailed format function for expanded view - keeps all the rich formatting
 */
export const formatValue = (value: any, key: string): React.ReactNode => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return <Typography component="span" color="text.secondary" fontStyle="italic">No value</Typography>;
  }
  
  // Handle boolean values
  if (typeof value === 'boolean') {
    return (
      <Typography component="span" color={value ? "success.main" : "text.secondary"}>
        {value ? 'Yes' : 'No'}
      </Typography>
    );
  }
  
  // Special handling for merged "_other" field values
  if (typeof value === 'string' && (value.includes(' | Other: ') || value.startsWith('Other: '))) {
    if (value.includes(' | Other: ')) {
      // Split on the separator and format both parts
      const [mainValue, otherPart] = value.split(' | Other: ');
      return (
        <Box component="span">
          <Typography component="span" sx={{ fontWeight: 'medium' }}>
            {mainValue}
          </Typography>
          <Typography component="span" sx={{ 
            display: 'block', 
            mt: 0.5, 
            color: 'info.main', 
            fontStyle: 'italic',
            fontSize: '0.9em'
          }}>
            ✏️ Other: {otherPart}
          </Typography>
        </Box>
      );
    } else if (value.startsWith('Other: ')) {
      // Only "other" value provided
      const otherValue = value.replace('Other: ', '');
      return (
        <Box component="span">
          <Typography component="span" sx={{ 
            color: 'info.main', 
            fontStyle: 'italic',
            fontWeight: 'medium'
          }}>
            ✏️ Other: {otherValue}
          </Typography>
        </Box>
      );
    }
  }
  
  // Handle arrays - special formatting for Fulcrum data types
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Typography component="span" color="text.secondary" fontStyle="italic">Empty list</Typography>;
    }
    
    // Special handling for Fulcrum photo arrays
    if (value.some(item => item && typeof item === 'object' && 'photo_id' in item)) {
      return (
        <Box component="span">
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', display: 'block', mb: 1 }}>
            📸 Photos ({value.length})
          </Typography>
          {value.map((photo: any, index: number) => (
            <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Photo {index + 1}: {photo.photo_id}
              </Typography>
              {photo.caption && (
                <Typography variant="body2" color="text.secondary">
                  Caption: {photo.caption}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      );
    }
    
    // Special handling for Fulcrum record link arrays
    if (value.some(item => item && typeof item === 'object' && 'record_id' in item)) {
      return (
        <Box component="span">
          <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 'bold', display: 'block', mb: 1 }}>
            🔗 Linked Records ({value.length})
          </Typography>
          {value.map((link: any, index: number) => (
            <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'secondary.light', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Record {index + 1}: {link.record_id}
              </Typography>
              {link.form_id && (
                <Typography variant="body2" color="text.secondary">
                  Form: {link.form_id}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      );
    }
    
    // Regular array handling - use chips for simple values
    return (
      <Box component="span">
        <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 'bold', display: 'block', mb: 0.5 }}>
          📋 List ({value.length} items)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.map((item, index) => {
            const itemStr = String(item);
            const isOtherValue = itemStr.startsWith('Other: ');
            return (
              <CMChip 
                key={index} 
                label={isOtherValue ? `✏️ ${itemStr}` : itemStr}
                colorScheme={isOtherValue ? "primary" : "secondary"} 
                sx={{ 
                  fontSize: '0.75rem',
                  fontStyle: isOtherValue ? 'italic' : 'normal'
                }} 
              />
            );
          })}
        </Box>
      </Box>
    );
  }
  
  // Handle objects - special formatting for Fulcrum data types
  if (typeof value === 'object') {
    // Special handling for Fulcrum geometry objects
    if (value.type && value.coordinates) {
      return (
        <Box component="span">
          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold', display: 'block' }}>
            🗺️ Geometry: {value.type}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8em', mt: 0.5 }}>
            {value.type === 'Point' ? 
              `Lat: ${value.coordinates[1]?.toFixed(6)}, Lng: ${value.coordinates[0]?.toFixed(6)}` :
              `${JSON.stringify(value, null, 2)}`
            }
          </Typography>
        </Box>
      );
    }
    
    // Regular object handling
    return (
      <Box component="span">
        <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 'bold', display: 'block' }}>
          📦 Object ({Object.keys(value).length} properties)
        </Typography>
        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75em', maxHeight: 150, overflow: 'auto', display: 'block', mt: 0.5, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
          {JSON.stringify(value, null, 2)}
        </Typography>
      </Box>
    );
  }
  
  // Special handling for URLs
  if (
    typeof value === 'string' &&
    (key.toLowerCase().includes('url') || key.toLowerCase().includes('link')) &&
    (value.startsWith('http') || value.startsWith('/'))
  ) {
    return (
      <Button
        component="a"
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        startIcon={<LinkIcon />}
        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
      >
        View Link
      </Button>
    );
  }
  
  // Special handling for dates
  if (
    typeof value === 'string' &&
    (key.toLowerCase().includes('date') || key.toLowerCase().includes('time') || key.toLowerCase().includes('created') || key.toLowerCase().includes('updated')) &&
    !isNaN(Date.parse(value))
  ) {
    const date = new Date(value);
    return (
      <Box component="span">
        <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 'bold', display: 'block' }}>
          📅 Date/Time
        </Typography>
        <Typography component="span">
          {date.toLocaleDateString()} {date.toLocaleTimeString()}
        </Typography>
      </Box>
    );
  }
  
  // Special handling for Fulcrum field IDs (4-character hex codes)
  if (typeof value === 'string' && /^[a-f0-9]{4}$/i.test(key)) {
    return (
      <Box component="span">
        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', display: 'block' }}>
          🏷️ Fulcrum Field ({key})
        </Typography>
        <Typography component="span" sx={{ fontWeight: 'medium' }}>
          {String(value)}
        </Typography>
      </Box>
    );
  }
  
  // Special handling for long text values
  if (typeof value === 'string' && value.length > 100) {
    return (
      <Box component="span">
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          📄 Long Text ({value.length} characters)
        </Typography>
        <Typography component="span" sx={{ 
          maxHeight: 100, 
          overflow: 'auto', 
          display: 'block', 
          p: 1, 
          bgcolor: 'grey.50', 
          borderRadius: 1, 
          mt: 0.5,
          fontSize: '0.9em'
        }}>
          {String(value)}
        </Typography>
      </Box>
    );
  }
  
  // Default - return as enhanced string with type indicator
  return (
    <Box component="span">
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7em' }}>
        {typeof value} • {String(value).length} chars
      </Typography>
      <Typography component="span" sx={{ display: 'block', fontWeight: String(value).length < 50 ? 'medium' : 'normal' }}>
        {String(value)}
      </Typography>
    </Box>
  );
};

// ============================================================================
// Form Data Processing Utilities  
// ============================================================================

/**
 * Extract and merge form values with "_other" suffix handling
 */
export const processFormValues = (formValues: Record<string, any>): Map<string, any> => {
  const allFormValues = Object.entries(formValues).filter(([key, value]) => 
    value !== null && value !== undefined && value !== ''
  );
  
  // Merge fields with "_other" suffix
  const mergedFormValues = new Map<string, any>();
  const processedKeys = new Set<string>();
  
  allFormValues.forEach(([key, value]) => {
    if (processedKeys.has(key)) return;
    
    const otherKey = `${key}_other`;
    const hasOtherField = allFormValues.some(([k]) => k === otherKey);
    
    if (hasOtherField) {
      const otherValue = allFormValues.find(([k]) => k === otherKey)?.[1];
      
      let mergedValue;
      if (value && otherValue) {
        if (Array.isArray(value)) {
          mergedValue = [...value, `Other: ${otherValue}`];
        } else {
          mergedValue = `${value} | Other: ${otherValue}`;
        }
      } else if (otherValue) {
        mergedValue = `Other: ${otherValue}`;
      } else {
        mergedValue = value;
      }
      
      mergedFormValues.set(key, mergedValue);
      processedKeys.add(key);
      processedKeys.add(otherKey);
    } else if (!key.endsWith('_other')) {
      mergedFormValues.set(key, value);
      processedKeys.add(key);
    }
  });
  
  return mergedFormValues;
};

/**
 * Legacy system field detection - now replaced by adapter priority system
 * @deprecated Use form_values_priority from Fulcrum adapter instead
 */
export const getSystemFieldNames = (): Set<string> => {
  // Only used for non-Fulcrum items now
  return new Set([
    'fulcrum_id', 'created_at', 'updated_at', 'created_by', 'updated_by', 
    'system_created_at', 'system_updated_at', 'version',
    'source_id', 'source_account', 'gps_altitude', 'gps_horizontal_accuracy', 
    'gps_vertical_accuracy', 'gps_speed', 'gps_course',
    'status', 'marker-color', 'latitude', 'longitude',
    'photos', 'photos_caption', 'videos'
  ]);
};

// ============================================================================
// Main Components
// ============================================================================

/**
 * Render Form Data fields in main view - clean and minimal
 * Uses priority information from Fulcrum adapter for consistent field filtering
 */
export const FormDataMainView: React.FC<FormDataMainViewProps> = ({ item }) => {
  const isFulcrumItem = !!(item as any).fulcrum_data;
  
  // Debug logging for troubleshooting
  console.log('[FormDataMainView] Debug:', {
    itemId: (item as any).id,
    isFulcrumItem,
    hasFulcrumData: !!(item as any).fulcrum_data,
    fulcrumDataKeys: (item as any).fulcrum_data ? Object.keys((item as any).fulcrum_data) : null,
    hasFormValues: !!(item as any).fulcrum_data?.form_values,
    formValuesKeys: (item as any).fulcrum_data?.form_values ? Object.keys((item as any).fulcrum_data.form_values) : null,
    hasPriorityMapping: !!(item as any).fulcrum_data?.form_values_priority,
    priorityMappingKeys: (item as any).fulcrum_data?.form_values_priority ? Object.keys((item as any).fulcrum_data.form_values_priority) : null
  });
  
  if (!isFulcrumItem) return null;
  
  const fulcrumData = (item as any).fulcrum_data;
  if (!fulcrumData?.form_values) return null;
  
  // Extract form values and their priority information from adapter
  const mergedFormValues = processFormValues(fulcrumData.form_values);
  const priorityMapping = fulcrumData.form_values_priority || {};
  
  if (mergedFormValues.size === 0) return null;
  
  // Use adapter's priority system instead of local filtering
  // Only show 'high' priority fields in main view
  const highPriorityFields = Array.from(mergedFormValues.entries()).filter(([key]) => {
    const priority = priorityMapping[key] || 'low';
    return priority === 'high' && !key.startsWith('_');
  });
  
  console.log('[FormDataMainView] Priority filtering result:', {
    totalFields: mergedFormValues.size,
    highPriorityFields: highPriorityFields.length,
    fieldPriorities: Object.entries(priorityMapping).slice(0, 5),
    sampleHighPriorityFields: highPriorityFields.slice(0, 3).map(([key, value]) => ({ key, value, type: typeof value }))
  });
  
  if (highPriorityFields.length === 0) return null;
  
  return (
    <Box sx={{ 
      mt: 3, 
      p: 2,
      backgroundColor: 'grey.50',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'grey.200'
    }}>
      <Stack spacing={1.5}>
        {highPriorityFields.map(([key, value]: [string, any]) => (
          <Box
            key={`main_${key}`}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 2,
              p: 1.5,
              backgroundColor: 'white',
              borderRadius: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              border: '1px solid',
              borderColor: 'grey.100',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                borderColor: 'primary.light',
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                minWidth: '120px',
                flex: '0 0 auto',
                fontSize: '0.8rem',
                textTransform: 'capitalize'
              }}
            >
              {key.replace(/_/g, ' ')}
            </Typography>
            <Box sx={{ 
              flex: 1,
              minWidth: 0,
              '& .MuiTypography-root': {
                fontSize: '0.875rem',
                lineHeight: 1.4,
                color: 'text.primary'
              }
            }}>
              {formatValueSimple(value, key)}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

/**
 * Render comprehensive additional properties with organized sections
 */
export const AttributesSection: React.FC<AttributeRenderingProps> = ({ 
  item, 
  showAdditionalProperties, 
  onToggleAdditionalProperties 
}) => {
  // Define system/metadata fields that should be de-emphasized
  const systemFieldNames = getSystemFieldNames();
  
  // For Fulcrum items, we want to show form values organized by type
  const isFulcrumItem = !!(item as any).fulcrum_data;
  let fulcrumUserFields: [string, any][] = [];
  let fulcrumSystemFields: [string, any][] = [];
  let fulcrumMetadata: [string, any][] = [];
  
  if (isFulcrumItem) {
    const fulcrumData = (item as any).fulcrum_data;
    
    // Extract and categorize form values using adapter's priority system
    if (fulcrumData.form_values) {
      const mergedFormValues = processFormValues(fulcrumData.form_values);
      const priorityMapping = fulcrumData.form_values_priority || {};
      const mergedEntries = Array.from(mergedFormValues.entries());
      
      // Use adapter's priority system for consistent categorization
      fulcrumUserFields = mergedEntries.filter(([key]) => {
        const priority = priorityMapping[key] || 'low';
        return priority === 'high' && !key.startsWith('_');
      });
      
      fulcrumSystemFields = mergedEntries.filter(([key]) => {
        const priority = priorityMapping[key] || 'low';
        return priority === 'low' && !key.startsWith('_');
      });
    }
    
    // Extract Fulcrum metadata (excluding form_values since we show it separately)
    fulcrumMetadata = Object.entries(fulcrumData).filter(([key, value]) => 
      key !== 'form_values' && value !== null && value !== undefined && value !== ''
    );
  }
  
  // Basic common properties that are always excluded
  const baseCommonProps = [
    "id",
    "name",
    "description",
    "primary_image",
    "file",
    "relationships", // Keep this to exclude the relationships object itself
  ];

  // Filter regular additional props
  const filteredAdditionalProps = Object.entries(item).filter(([key, value]) => {
    // Skip fields that start with underscore or are empty/null
    if (key.startsWith('_') || value === null || value === undefined || value === '') {
      return false;
    }
    
    // Skip common properties
    if (baseCommonProps.includes(key.toLowerCase())) {
      return false;
    }
    
    // For Fulcrum items, we'll show fulcrum_data separately, so exclude it here
    if (isFulcrumItem && key === 'fulcrum_data') {
      return false;
    }
    
    return true;
  });

  const totalPropsToShow = filteredAdditionalProps.length + fulcrumUserFields.length + 
                           fulcrumSystemFields.length + fulcrumMetadata.length;
  
  if (totalPropsToShow === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        onClick={onToggleAdditionalProperties}
        startIcon={showAdditionalProperties ? '−' : '+'}
        variant="outlined"
        size="small"
        fullWidth
        sx={{ 
          mb: 2,
          justifyContent: 'center',
          color: 'primary.main',
          borderColor: 'primary.main',
          '&:hover': {
            backgroundColor: 'primary.light',
            borderColor: 'primary.main',
          }
        }}
      >
        {showAdditionalProperties ? 'Hide' : 'Show'} All Attributes ({totalPropsToShow}
        {isFulcrumItem && fulcrumUserFields.length > 0 && ` • ${fulcrumUserFields.length} User Fields`}
        {isFulcrumItem && fulcrumSystemFields.length > 0 && ` • ${fulcrumSystemFields.length} System Fields`})
      </Button>
      
      <Fade in={showAdditionalProperties}>
        <Box
          sx={{
            display: showAdditionalProperties ? 'block' : 'none',
            maxHeight: 500,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            backgroundColor: 'grey.50'
          }}
        >
          <Stack spacing={2}>
            
            {/* Fulcrum User Form Fields - Show this first as it's most important */}
            {isFulcrumItem && fulcrumUserFields.length > 0 && (
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main', 
                    mb: 1.5,
                    borderBottom: '2px solid',
                    borderColor: 'primary.main',
                    pb: 0.5
                  }}
                >
                  📋 Form Data ({fulcrumUserFields.length} fields)
                </Typography>
                <Stack spacing={1.5}>
                  {fulcrumUserFields.map(([key, value]) => (
                    <Box
                      key={`form_${key}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: 2,
                        p: 1.5,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          color: 'primary.dark',
                          minWidth: '140px',
                          flex: '0 0 auto',
                          wordBreak: 'break-word'
                        }}
                      >
                        {key}:
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" component="div">
                          {formatValue(value, key)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Fulcrum System Fields - Show separately with less prominence */}
            {isFulcrumItem && fulcrumSystemFields.length > 0 && (
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'text.secondary', 
                    mb: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'text.secondary',
                    pb: 0.5
                  }}
                >
                  🔧 System & Metadata Fields ({fulcrumSystemFields.length} fields)
                </Typography>
                <Stack spacing={1}>
                  {fulcrumSystemFields.map(([key, value]) => (
                    <Box
                      key={`system_${key}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: 2,
                        p: 1,
                        backgroundColor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.300'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'medium',
                          color: 'text.secondary',
                          minWidth: '140px',
                          flex: '0 0 auto',
                          wordBreak: 'break-word',
                          fontSize: '0.8em'
                        }}
                      >
                        {key}:
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" component="div" sx={{ fontSize: '0.8em' }}>
                          {formatValue(value, key)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Regular Additional Properties Section */}
            {filteredAdditionalProps.length > 0 && (
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'secondary.main', 
                    mb: 1.5,
                    borderBottom: '2px solid',
                    borderColor: 'secondary.main',
                    pb: 0.5
                  }}
                >
                  📊 General Properties ({filteredAdditionalProps.length} fields)
                </Typography>
                <Stack spacing={1.5}>
                  {filteredAdditionalProps.map(([key, value]) => (
                    <Box
                      key={`regular_${key}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: 2
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          color: 'secondary.main',
                          minWidth: '120px',
                          flex: '0 0 auto',
                          wordBreak: 'break-word'
                        }}
                      >
                        {formatFieldName(key)}:
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" component="div">
                          {formatValue(value, key)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Fulcrum Metadata Section */}
            {isFulcrumItem && fulcrumMetadata.length > 0 && (
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'info.main', 
                    mb: 1.5,
                    borderBottom: '2px solid',
                    borderColor: 'info.main',
                    pb: 0.5
                  }}
                >
                  🔧 Fulcrum Metadata ({fulcrumMetadata.length} fields)
                </Typography>
                <Stack spacing={1.5}>
                  {fulcrumMetadata.map(([key, value]) => (
                    <Box
                      key={`meta_${key}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: 2
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          color: 'info.main',
                          minWidth: '120px',
                          flex: '0 0 auto',
                          wordBreak: 'break-word'
                        }}
                      >
                        {formatFieldName(key)}:
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" component="div">
                          {formatValue(value, key)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
            
          </Stack>
        </Box>
      </Fade>
    </Box>
  );
}; 